import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  runTransaction,
  Timestamp,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../config/firebase";

/* ─── Service-specific default availability slots ─────────────── */
function getServiceDefaultSlots(serviceId) {
  switch (serviceId) {
    case "jet-ski":
      return [
        { time: "09:00 - 10:00", capacity: 2, booked: 0 },
        { time: "11:00 - 12:00", capacity: 2, booked: 0 },
        { time: "16:00 - 17:00", capacity: 2, booked: 0 },
        { time: "18:00 - 19:00", capacity: 2, booked: 0 },
      ];
    case "luxury-yacht":
      return [
        { time: "10:00 - 14:00", capacity: 10, booked: 0 },
        { time: "16:00 - 20:00", capacity: 10, booked: 0 },
      ];
    case "bayliner": // all possible slots across both packages
      return [
        { time: "09:00 - 09:30", capacity: 5, booked: 0 },
        { time: "09:00 - 10:00", capacity: 5, booked: 0 },
        { time: "10:00 - 10:30", capacity: 5, booked: 0 },
        { time: "11:00 - 11:30", capacity: 5, booked: 0 },
        { time: "11:00 - 12:00", capacity: 5, booked: 0 },
        { time: "14:00 - 14:30", capacity: 5, booked: 0 },
        { time: "14:00 - 15:00", capacity: 5, booked: 0 },
        { time: "15:00 - 15:30", capacity: 5, booked: 0 },
        { time: "16:00 - 16:30", capacity: 5, booked: 0 },
        { time: "16:00 - 17:00", capacity: 5, booked: 0 },
        { time: "17:00 - 17:30", capacity: 5, booked: 0 },
      ];
    default: // speedboat — ALL possible slots across all 4 packages
      // One physical boat: slots must not overlap.
      // Pkg 3 Boat Cruise (90 min): 09:00-10:30, 12:00-13:30, 15:00-16:30, 17:00-18:30, 19:00-20:30
      // Pkg 4 Extended (120 min): 09:00-11:00, 18:00-20:00
      return [
        { time: "09:00 - 10:30", capacity: 11, booked: 0 },
        { time: "09:00 - 11:00", capacity: 11, booked: 0 },
        { time: "12:00 - 13:30", capacity: 11, booked: 0 },
        { time: "15:00 - 16:30", capacity: 11, booked: 0 },
        { time: "17:00 - 18:30", capacity: 11, booked: 0 },
        { time: "18:00 - 20:00", capacity: 11, booked: 0 },
        { time: "19:00 - 20:30", capacity: 11, booked: 0 },
      ];
  }
}

/* ─── Overlap helpers ─────────────────────────────────────────── */
// Convert "HH:MM" to minutes since midnight
function toMin(hhmm) {
  const [h, m] = hhmm.trim().split(":").map(Number);
  return h * 60 + m;
}
// Returns true when two "HH:MM - HH:MM" slot strings share any time
function slotsOverlap(timeA, timeB) {
  if (!timeA || !timeB || timeA === timeB) return false;
  const [sA, eA] = timeA.split(" - ").map(toMin);
  const [sB, eB] = timeB.split(" - ").map(toMin);
  return sA < eB && sB < eA;
}

/**
 * Firestore Collections Structure:
 *
 * bookings: {
 *   id, userId, serviceId, serviceType, date, timeSlot,
 *   numberOfGuests, totalPrice, status, paymentId, createdAt, extras
 * }
 *
 * availability: {
 *   id, serviceId, date, totalCapacity, bookedCapacity,
 *   timeSlots: [{ time, capacity, booked }]
 * }
 *
 * services: {
 *   id, type, name, capacity, price, bookingRules
 * }
 */

class BookingService {
  constructor() {
    this.bookingsRef = collection(db, "bookings");
    this.availabilityRef = collection(db, "availability");
    this.servicesRef = collection(db, "services");
    this.externalBookingsRef = collection(db, "externalBookings");
  }

  /**
   * Check real-time availability for a service on a specific date and time
   */
  async checkAvailability(
    serviceId,
    date,
    timeSlot = null,
    numberOfGuests = 1,
  ) {
    try {
      const dateStr = this.formatDate(date);
      const availabilityDoc = await this.getOrCreateAvailability(
        serviceId,
        dateStr,
      );

      if (!availabilityDoc) {
        return { available: false, message: "Service not found" };
      }

      // If time slot is specified, check specific slot
      if (timeSlot) {
        const slot = availabilityDoc.timeSlots?.find(
          (s) => s.time === timeSlot,
        );
        if (!slot) {
          return { available: false, message: "Time slot not found" };
        }

        const available = slot.capacity - slot.booked >= numberOfGuests;
        return {
          available,
          remainingCapacity: slot.capacity - slot.booked,
          message: available ? "Available" : "Not enough capacity",
        };
      }

      // Check overall capacity for the day
      const available =
        availabilityDoc.totalCapacity - availabilityDoc.bookedCapacity >=
        numberOfGuests;
      return {
        available,
        remainingCapacity:
          availabilityDoc.totalCapacity - availabilityDoc.bookedCapacity,
        message: available ? "Available" : "Fully booked",
        timeSlots: availabilityDoc.timeSlots || [],
      };
    } catch (error) {
      console.error("Error checking availability:", error);
      throw error;
    }
  }

  /**
   * Generate unique booking reference
   * Format: BK + 6 digits (e.g., BK789234)
   */
  generateBookingReference() {
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, "0");
    return `BK${timestamp.slice(0, 3)}${random}`;
  }

  /**
   * Check if booking reference already exists
   */
  async bookingReferenceExists(reference) {
    const q = query(
      this.bookingsRef,
      where("bookingReference", "==", reference),
      limit(1),
    );
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  }

  /**
   * Generate unique booking reference (ensures no duplicates)
   */
  async generateUniqueBookingReference() {
    let reference;
    let attempts = 0;
    const maxAttempts = 10;

    do {
      reference = this.generateBookingReference();
      attempts++;

      if (attempts >= maxAttempts) {
        // If we've tried too many times, add more randomness
        reference = `BK${Date.now().toString().slice(-4)}${Math.floor(Math.random() * 100)}`;
        break;
      }
    } while (await this.bookingReferenceExists(reference));

    return reference;
  }

  /**
   * Create booking with transaction to prevent overbooking
   * This uses Firestore transactions to ensure atomic operations
   */
  async createBooking(bookingData) {
    const {
      userId,
      serviceId,
      serviceType,
      date,
      timeSlot,
      numberOfGuests,
      totalPrice,
      extras = {},
      customerInfo,
    } = bookingData;

    try {
      // Generate unique booking reference
      const bookingReference = await this.generateUniqueBookingReference();

      // Ensure availability doc exists before the transaction
      const dateStr = this.formatDate(date);
      await this.getOrCreateAvailability(serviceId, dateStr);

      // Use transaction to prevent race conditions
      const result = await runTransaction(db, async (transaction) => {
        const dateStr = this.formatDate(date);

        // Get availability document
        const availabilityQuery = query(
          this.availabilityRef,
          where("serviceId", "==", serviceId),
          where("date", "==", dateStr),
        );
        const availabilitySnapshot = await getDocs(availabilityQuery);

        if (availabilitySnapshot.empty) {
          throw new Error("Availability not found for this date");
        }

        const availabilityDocRef = availabilitySnapshot.docs[0].ref;
        const availabilityData = availabilitySnapshot.docs[0].data();

        // Check availability within transaction
        if (timeSlot) {
          let slots = availabilityData.timeSlots;

          // If the booked time slot isn't in the stored list, MERGE the full default
          // set with existing booked counts — never wipe existing bookings.
          if (
            !slots ||
            slots.length === 0 ||
            !slots.find((s) => s.time === timeSlot)
          ) {
            const defaultSlots = getServiceDefaultSlots(serviceId);
            const existingMap = new Map((slots || []).map((s) => [s.time, s]));
            slots = defaultSlots.map((ds) => existingMap.get(ds.time) || ds);
            transaction.update(availabilityDocRef, { timeSlots: slots });
          }

          const slotIndex = slots.findIndex((s) => s.time === timeSlot);
          if (slotIndex === -1) {
            throw new Error(
              `Time slot "${timeSlot}" not found for ${serviceId}.`,
            );
          }

          const slot = slots[slotIndex];

          // ONE-BOAT RULE:
          // • Slots with the SAME time string = same departure trip (e.g. Shipwreck +
          //   Coastal both at "09:00 - 10:30" → they share the boat, share 11 seats).
          // • Slots with a DIFFERENT but overlapping time string = different tour type
          //   (e.g. Express "09:00 - 10:00" vs Shipwreck "09:00 - 10:30") → the boat
          //   is committed to one trip; any booking in the conflicting slot blocks this.
          if (serviceId === "speedboat" || serviceId === "bayliner") {
            const conflictSlot = slots.find(
              (s) =>
                s.time !== timeSlot &&
                (s.booked || 0) > 0 &&
                slotsOverlap(s.time, timeSlot),
            );
            if (conflictSlot) {
              throw new Error(
                `The boat is already running a different tour at this time (${conflictSlot.time}). Please choose another time slot.`,
              );
            }
          }

          // For same-time slots (Shipwreck + Coastal sharing the trip): sum their bookings
          const sameTripBooked =
            serviceId === "speedboat"
              ? slots
                  .filter((s) => s.time === timeSlot)
                  .reduce((sum, s) => sum + (s.booked || 0), 0)
              : slot.booked; // bayliner/others: no same-trip sharing

          if (slot.capacity - sameTripBooked < numberOfGuests) {
            throw new Error(
              `Not enough capacity. ${slot.capacity - sameTripBooked} spot(s) remaining.`,
            );
          }

          // Slot is available — availability will be decremented only after
          // payment is confirmed (in the bktCallback server function).
          // Do NOT update availability here so pending/abandoned payments
          // do not block the slot.
        } else {
          // No time slot specified — same rule: only block after payment.
          if (
            availabilityData.totalCapacity - availabilityData.bookedCapacity <
            numberOfGuests
          ) {
            throw new Error("Not enough capacity for this date");
          }
          // Availability updated on payment confirmation, not here.
        }

        // Create booking document
        const bookingRef = doc(this.bookingsRef);
        const booking = {
          id: bookingRef.id,
          bookingReference,
          userId,
          serviceId,
          serviceType,
          date: dateStr,
          timeSlot: timeSlot || null,
          numberOfGuests,
          totalPrice,
          extras,
          customerInfo,
          status: "pending", // pending, confirmed, cancelled, completed
          paymentStatus: "pending", // pending, paid, refunded
          paymentId: null,
          source: "direct", // direct, getyourguide, tripadvisor
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };

        transaction.set(bookingRef, booking);

        return { bookingId: bookingRef.id, booking, bookingReference };
      });

      return result;
    } catch (error) {
      console.error("Error creating booking:", error);
      throw error;
    }
  }

  /**
   * Update booking status (confirm, cancel, etc.)
   */
  async updateBookingStatus(bookingId, status, paymentId = null) {
    try {
      const bookingRef = doc(this.bookingsRef, bookingId);
      const bookingDoc = await getDoc(bookingRef);

      if (!bookingDoc.exists()) {
        throw new Error("Booking not found");
      }

      const updateData = {
        status,
        updatedAt: Timestamp.now(),
      };

      if (paymentId) {
        updateData.paymentId = paymentId;
        updateData.paymentStatus = "paid";
      }

      // If cancelling, release the capacity
      if (status === "cancelled") {
        const booking = bookingDoc.data();
        await this.releaseCapacity(
          booking.serviceId,
          booking.date,
          booking.timeSlot,
          booking.numberOfGuests,
        );
      }

      await updateDoc(bookingRef, updateData);
      return { success: true, bookingId };
    } catch (error) {
      console.error("Error updating booking:", error);
      throw error;
    }
  }

  /**
   * Release capacity when a booking is cancelled
   */
  async releaseCapacity(serviceId, date, timeSlot, numberOfGuests) {
    try {
      await runTransaction(db, async (transaction) => {
        const availabilityQuery = query(
          this.availabilityRef,
          where("serviceId", "==", serviceId),
          where("date", "==", date),
        );
        const availabilitySnapshot = await getDocs(availabilityQuery);

        if (!availabilitySnapshot.empty) {
          const availabilityDocRef = availabilitySnapshot.docs[0].ref;
          const availabilityData = availabilitySnapshot.docs[0].data();

          if (timeSlot) {
            const slotIndex = availabilityData.timeSlots?.findIndex(
              (s) => s.time === timeSlot,
            );
            if (slotIndex !== -1) {
              const updatedSlots = [...availabilityData.timeSlots];
              updatedSlots[slotIndex] = {
                ...updatedSlots[slotIndex],
                booked: Math.max(
                  0,
                  updatedSlots[slotIndex].booked - numberOfGuests,
                ),
              };

              transaction.update(availabilityDocRef, {
                timeSlots: updatedSlots,
                bookedCapacity: Math.max(
                  0,
                  availabilityData.bookedCapacity - numberOfGuests,
                ),
              });
            }
          } else {
            transaction.update(availabilityDocRef, {
              bookedCapacity: Math.max(
                0,
                availabilityData.bookedCapacity - numberOfGuests,
              ),
            });
          }
        }
      });
    } catch (error) {
      console.error("Error releasing capacity:", error);
      throw error;
    }
  }

  /**
   * Get bookings for a user
   */
  async getUserBookings(userId, statusFilter = null) {
    try {
      let q = query(
        this.bookingsRef,
        where("userId", "==", userId),
        orderBy("createdAt", "desc"),
      );

      if (statusFilter) {
        q = query(q, where("status", "==", statusFilter));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Error getting user bookings:", error);
      throw error;
    }
  }

  /**
   * Get availability for a date range
   */
  async getAvailabilityRange(serviceId, startDate, endDate) {
    try {
      const startStr = this.formatDate(startDate);
      const endStr = this.formatDate(endDate);

      const q = query(
        this.availabilityRef,
        where("serviceId", "==", serviceId),
        where("date", ">=", startStr),
        where("date", "<=", endStr),
        orderBy("date"),
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Error getting availability range:", error);
      throw error;
    }
  }

  /**
   * Initialize availability for a service and date
   */
  async getOrCreateAvailability(serviceId, dateStr) {
    try {
      const q = query(
        this.availabilityRef,
        where("serviceId", "==", serviceId),
        where("date", "==", dateStr),
      );
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
      }

      const defaultSlots = getServiceDefaultSlots(serviceId);
      const totalCapacity = defaultSlots.reduce(
        (sum, s) => sum + s.capacity,
        0,
      );

      const newAvailability = {
        serviceId,
        date: dateStr,
        totalCapacity,
        bookedCapacity: 0,
        timeSlots: defaultSlots,
        createdAt: Timestamp.now(),
      };

      const docRef = await addDoc(this.availabilityRef, newAvailability);
      return { id: docRef.id, ...newAvailability };
    } catch (error) {
      console.error("Error getting/creating availability:", error);
      throw error;
    }
  }

  /**
   * Helper to format dates consistently
   */
  formatDate(date) {
    const d = new Date(date);
    return d.toISOString().split("T")[0]; // YYYY-MM-DD
  }

  /**
   * Get booking by reference and email for verification
   */
  async getBookingByReferenceAndEmail(bookingReference, email) {
    return this.getBookingByReferenceAndContact(bookingReference, email, "");
  }

  /**
   * Get booking by reference + email OR phone (at least one required).
   * Searches regular bookings first, then external bookings.
   */
  async getBookingByReferenceAndContact(bookingReference, email, phone) {
    try {
      const ref = bookingReference.trim();
      // Normalise inputs: lowercase email, strip all non-digits from phone
      const emailNorm = (email || "").trim().toLowerCase();
      // Keep only digits so +355 69 123 4567 == 069 123 4567 == 00355691234567 etc.
      const phoneDigits = (phone || "").replace(/\D/g, "");

      // Helper: compare phones by last 10 significant digits (handles any country code format)
      const phonesMatch = (a, b) => {
        if (!a || !b) return false;
        const da = a.replace(/\D/g, "");
        const db = b.replace(/\D/g, "");
        if (!da || !db) return false;
        const len = Math.min(da.length, db.length, 10);
        return da.slice(-len) === db.slice(-len);
      };

      // ── 1. Search regular bookings ──────────────────────────────
      const q = query(
        this.bookingsRef,
        where("bookingReference", "==", ref.toUpperCase()),
        limit(1),
      );
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const bookingDoc = snapshot.docs[0];
        const booking = { id: bookingDoc.id, ...bookingDoc.data() };

        const storedEmail = (booking.customerInfo?.email || "")
          .trim()
          .toLowerCase();
        const storedPhone =
          booking.customerInfo?.phone || booking.customerInfo?.contact || "";
        const noContactStored = !storedEmail && !storedPhone;

        // If no contact is on file, or at least one provided value matches → success
        if (
          noContactStored ||
          (emailNorm && storedEmail && storedEmail === emailNorm) ||
          (phoneDigits && phonesMatch(storedPhone, phone))
        ) {
          return booking;
        }

        // Reference found but contact wrong — do NOT fall through to external
        return null;
      }

      // ── 2. Reference not in regular bookings → search external ──
      // Admin panel displays references as "EXT-<referenceId>"; strip that prefix.
      const stripped = ref.replace(/^EXT-/i, "");
      const refVariants = Array.from(
        new Set(
          [
            ref,
            ref.toUpperCase(),
            ref.toLowerCase(),
            stripped,
            stripped.toUpperCase(),
            stripped.toLowerCase(),
          ].filter(Boolean),
        ),
      );

      for (const refVariant of refVariants) {
        const extQ = query(
          this.externalBookingsRef,
          where("referenceId", "==", refVariant),
          limit(1),
        );
        const extSnapshot = await getDocs(extQ);
        if (extSnapshot.empty) continue;

        const extDoc = extSnapshot.docs[0];
        const extData = extDoc.data();

        const extEmail = (extData.guestEmail || "").trim().toLowerCase();
        const extPhone = extData.guestPhone || "";
        const noContactStored = !extEmail && !extPhone;

        const contactMatches =
          noContactStored ||
          (emailNorm && extEmail && extEmail === emailNorm) ||
          (phoneDigits && phonesMatch(extPhone, phone));

        if (!contactMatches) continue;

        // Normalise into the standard shape AddExtras expects
        return {
          id: extDoc.id,
          bookingReference: extData.referenceId,
          serviceId: extData.serviceId,
          serviceType: extData.serviceId,
          date: extData.date,
          timeSlot: extData.timeSlot || "",
          numberOfGuests: extData.guestCount || 0,
          totalPrice: extData.price || 0,
          status: extData.status,
          extras: extData.extras || { drinks: {}, snacks: {}, jetSki: false },
          customerInfo: {
            name: extData.guestName || "",
            email: extData.guestEmail || "",
            phone: extData.guestPhone || "",
          },
          _isExternal: true,
        };
      }

      return null;
    } catch (error) {
      console.error("Error getting booking by reference:", error);
      throw error;
    }
  }

  /**
   * Add extras to an existing booking.
   * Pass isExternal=true when the booking came from the externalBookings collection.
   */
  async addExtrasToBooking(
    bookingId,
    newExtras,
    extrasPrice,
    isExternal = false,
  ) {
    try {
      const collectionRef = isExternal
        ? this.externalBookingsRef
        : this.bookingsRef;
      const bookingRef = doc(collectionRef, bookingId);
      const bookingSnap = await getDoc(bookingRef);

      if (!bookingSnap.exists()) {
        throw new Error("Booking not found");
      }

      const currentBooking = bookingSnap.data();

      // Merge new extras with existing extras (handles drinks, snacks, food, jetSki)
      const updatedExtras = {
        drinks: {
          ...(currentBooking.extras?.drinks || {}),
          ...(newExtras.drinks || {}),
        },
        snacks: {
          ...(currentBooking.extras?.snacks || {}),
          ...(newExtras.snacks || {}),
        },
        food: {
          ...(currentBooking.extras?.food || {}),
          ...(newExtras.food || {}),
        },
        // jetSki: keep existing true, or set if newly added
        jetSki: currentBooking.extras?.jetSki || newExtras.jetSki || false,
      };

      // External bookings store revenue as `price`; regular bookings use `totalPrice`
      const priceUpdate = isExternal
        ? { price: (currentBooking.price || 0) + extrasPrice }
        : { totalPrice: (currentBooking.totalPrice || 0) + extrasPrice };

      await updateDoc(bookingRef, {
        extras: updatedExtras,
        ...priceUpdate,
        updatedAt: Timestamp.now(),
      });

      return {
        success: true,
        bookingId,
        newTotalPrice: isExternal
          ? (currentBooking.price || 0) + extrasPrice
          : (currentBooking.totalPrice || 0) + extrasPrice,
      };
    } catch (error) {
      console.error("Error adding extras to booking:", error);
      throw error;
    }
  }

  /**
   * Sync booking from external platforms (GetYourGuide, TripAdvisor)
   */
  async syncExternalBooking(externalBookingData) {
    try {
      const {
        externalId,
        source, // 'getyourguide' or 'tripadvisor'
        serviceId,
        date,
        numberOfGuests,
        customerInfo,
      } = externalBookingData;

      // Check if already synced
      const q = query(
        this.bookingsRef,
        where("externalId", "==", externalId),
        where("source", "==", source),
        limit(1),
      );
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        return {
          success: true,
          message: "Booking already synced",
          bookingId: snapshot.docs[0].id,
        };
      }

      // Create booking
      const result = await this.createBooking({
        userId: "external",
        serviceId,
        serviceType: "external",
        date,
        numberOfGuests,
        customerInfo,
        totalPrice: 0, // Already paid externally
        extras: { externalId, source },
      });

      await updateDoc(doc(this.bookingsRef, result.bookingId), {
        externalId,
        source,
        status: "confirmed",
        paymentStatus: "paid",
      });

      return { success: true, bookingId: result.bookingId };
    } catch (error) {
      console.error("Error syncing external booking:", error);
      throw error;
    }
  }

  /**
   * Get blocked (fully booked) dates for a service in a date range
   * Returns array of dates that are NOT available for booking
   */
  async getBlockedDates(serviceId, startDate, endDate) {
    try {
      const startStr = this.formatDate(startDate);
      const endStr = this.formatDate(endDate);

      const q = query(
        this.availabilityRef,
        where("serviceId", "==", serviceId),
        where("date", ">=", startStr),
        where("date", "<=", endStr),
        orderBy("date"),
      );

      const snapshot = await getDocs(q);
      const blockedDates = [];

      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        // Check if all time slots are fully booked
        const allSlotsFull = data.timeSlots?.every(
          (slot) => slot.capacity - slot.booked === 0,
        );

        if (allSlotsFull || data.bookedCapacity >= data.totalCapacity) {
          blockedDates.push(data.date);
        }
      });

      return blockedDates;
    } catch (error) {
      console.error("Error getting blocked dates:", error);
      throw error;
    }
  }

  /**
   * Get available time slots for a specific date
   * Returns array of time slots with availability info
   */
  async getAvailableTimesForDate(serviceId, date, numberOfGuests = 1) {
    try {
      const dateStr = this.formatDate(date);
      const availabilityDoc = await this.getOrCreateAvailability(
        serviceId,
        dateStr,
      );

      if (!availabilityDoc || !availabilityDoc.timeSlots) {
        return [];
      }

      // Filter and map time slots with availability info
      return availabilityDoc.timeSlots
        .map((slot) => ({
          time: slot.time,
          capacity: slot.capacity,
          booked: slot.booked,
          available: slot.capacity - slot.booked,
          canBook: slot.capacity - slot.booked >= numberOfGuests,
        }))
        .filter((slot) => slot.available > 0); // Only return slots with availability
    } catch (error) {
      console.error("Error getting available times:", error);
      throw error;
    }
  }

  /**
   * Validate if a booking can be made
   * Returns {valid: boolean, message: string}
   */
  async validateBookingCapacity(serviceId, date, timeSlot, numberOfGuests) {
    try {
      const MAX_CAPACITY = 11;

      // Check if number of guests exceeds max
      if (numberOfGuests > MAX_CAPACITY) {
        return {
          valid: false,
          message: `Maximum capacity is ${MAX_CAPACITY} guests per booking`,
        };
      }

      // Check availability
      const availability = await this.checkAvailability(
        serviceId,
        date,
        timeSlot,
        numberOfGuests,
      );

      if (!availability.available) {
        return {
          valid: false,
          message: availability.message,
        };
      }

      return {
        valid: true,
        message: "Booking is available",
        remainingCapacity: availability.remainingCapacity,
      };
    } catch (error) {
      console.error("Error validating booking capacity:", error);
      return {
        valid: false,
        message: "Error checking availability",
      };
    }
  }
}

const bookingServiceInstance = new BookingService();
export default bookingServiceInstance;
